import { useCallback, useEffect, useRef, useState } from 'react'

import {
  deselectAsset,
  fetchAssets,
  fetchBundleStatus,
  fetchProject,
  requestBundle,
  requestDownloads,
  selectAsset,
} from '../../portal/api'
import { BookText, Download, ImageOff, Star } from 'lucide-react'

import { ApiException } from '../../api/client'
import { applyTheme } from '../../theme'
import {
  Button,
  EmptyState,
  ErrorState,
  GallerySkeleton,
  ICON_STROKE,
  Skeleton,
  cx,
} from '../../ui'
import type { PortalSession, SharedAsset, SharedProject } from '../../portal/types'
import { AssetTile } from './AssetTile'
import { DocumentList } from './DocumentList'
import { Lightbox } from './Lightbox'
import { SendFilesPanel } from './SendFilesPanel'

interface Props {
  session: PortalSession
  /** La entrega caducó con la pestaña abierta: hay pantalla propia para eso. */
  onExpired: () => void
}

/*
 | Filtro segmentado, no dos botones sueltos: así se lee como un interruptor de
 | dos posiciones y queda claro que son excluyentes. La pastilla activa se eleva
 | sobre el carril hundido, que es la señal que el ojo ya conoce.
 */
const FILTER_TAB = 'action rounded-full px-4 py-1.5 text-label transition-colors'
const FILTER_TAB_ON = 'bg-surface font-medium text-ink shadow-raised'
const FILTER_TAB_OFF = 'text-ink-muted hover:text-ink'

/**
 * El único fallo de apertura que se arregla recargando.
 *
 * Los demás vienen del servidor con su motivo —caducada, revocada— y ofrecer
 * «Reintentar» en ésos manda al cliente a pulsar un botón que nunca funciona.
 */
const RETRYABLE = 'No se pudo abrir la galería.'

export function GalleryPage({ session, onExpired }: Props) {
  const [project, setProject] = useState<SharedProject | null>(null)
  const [assets, setAssets] = useState<SharedAsset[]>([])
  /*
   | Lo decide el servidor, no el cliente.
   |
   | Se podría intentar deducir aquí —«si ninguno tiene miniatura, será una
   | lista»— y estaría mal: una página de planos recién subidos se parece
   | exactamente a una de fotos que el worker todavía no ha procesado, así que
   | la galería cambiaría de forma sola a los treinta segundos.
   */
  const [readsAsList, setReadsAsList] = useState(false)
  const [loading, setLoading] = useState(false)
  const [onlySelected, setOnlySelected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadNote, setDownloadNote] = useState<string | null>(null)
  const [quota, setQuota] = useState<{ used: number; max: number | null }>({ used: 0, max: null })
  const [viewerIndex, setViewerIndex] = useState<number | null>(null)

  const sentinelRef = useRef<HTMLDivElement>(null)

  // Cursor, filtro y banderas de carga viven en refs, no en estado.
  //
  // Si estuvieran en estado, `loadPage` cambiaría de identidad en cada render y
  // el IntersectionObserver se recrearía sin parar; y reiniciar la lista al
  // cambiar de filtro exigiría un efecto que llama a setState, que provoca
  // renders en cascada. Con refs, `loadPage` es estable y el reinicio ocurre en
  // el manejador del clic, que es donde el usuario lo provoca.
  const cursorRef = useRef<string | null>(null)
  const loadingRef = useRef(false)
  const exhaustedRef = useRef(false)
  const onlySelectedRef = useRef(false)

  /*
   | `view` es ver las FOTOS, y ahora se comprueba de verdad en el servidor.
   |
   | Un enlace de recepción sólo lleva `upload`: no hay galería que pintar y
   | pedirla devolvería 403. Esta pantalla se convierte entonces en la de
   | enviar archivos, con la marca del estudio, que es a lo que viene quien
   | abre esa dirección.
   */
  const canView = project?.permissions.includes('view') ?? false
  const canSelect = project?.permissions.includes('select') ?? false
  const canDownload = project?.permissions.includes('download') ?? false
  const canUpload = project?.permissions.includes('upload') ?? false
  const selectedCount = assets.filter((a) => a.selected).length

  // Branding del estudio: es el punto entero de la marca blanca. El cliente
  // final ve a su fotógrafo, no a nosotros.
  //
  // Antes esto escribía dos variables a mano, igual que useSession por su lado.
  // Ahora las dos rutas llaman al mismo motor, que además del color deriva la
  // rampa de grises, comprueba el contraste y aplica el tema elegido.
  useEffect(() => {
    if (!project) return

    applyTheme(project.studio.branding)
    document.title = `${project.name} · ${project.studio.name}`
  }, [project])

  const loadPage = useCallback(
    async (reset: boolean) => {
      if (loadingRef.current) return
      if (!reset && exhaustedRef.current) return

      loadingRef.current = true
      setLoading(true)

      try {
        const page = await fetchAssets(session.token, {
          cursor: reset ? null : cursorRef.current,
          onlySelected: onlySelectedRef.current,
        })

        setAssets((previous) => (reset ? page.data : [...previous, ...page.data]))
        setReadsAsList(page.reads_as_list)
        cursorRef.current = page.next_cursor
        exhaustedRef.current = page.next_cursor === null
      } catch {
        setError('No se pudieron cargar las fotos.')
      } finally {
        loadingRef.current = false
        setLoading(false)
      }
    },
    [session.token],
  )

  const changeFilter = useCallback(
    (next: boolean) => {
      if (next === onlySelectedRef.current) return

      onlySelectedRef.current = next
      cursorRef.current = null
      exhaustedRef.current = false
      setOnlySelected(next)
      void loadPage(true)
    },
    [loadPage],
  )

  useEffect(() => {
    void fetchProject(session.token)
      .then((p) => {
        setProject(p)
        setQuota(p.selection_quota)

        // Después de conocer los permisos, no antes: en un enlace de recepción
        // pedir las fotos es un 403, y el catch de abajo lo pintaría como «no
        // se pudieron cargar» sobre una pantalla que no tiene ninguna.
        if (p.permissions.includes('view')) {
          void loadPage(true)
        }
      })
      /*
       | El mensaje del servidor tal cual, no uno genérico.
       |
       | La sesión del portal dura dos horas, así que una entrega puede caducar
       | con la pestaña abierta. Decir «no se pudo abrir la galería» ahí es
       | achacarlo a un fallo cuando lo que pasa es que su fotógrafo puso fecha.
       */
      .catch((caught: unknown) => {
        // La entrega caducada tiene su propia pantalla en el portal: aquí
        // saldría bajo el título «no se pudo abrir la galería», que dice que
        // algo falló cuando lo que pasó es que llegó una fecha.
        if (caught instanceof ApiException && caught.error.code === 'share_project_expired') {
          onExpired()

          return
        }

        setError(caught instanceof ApiException ? caught.message : RETRYABLE)
      })
  }, [session.token, loadPage, onExpired])

  // Scroll infinito con IntersectionObserver en vez de escuchar el scroll:
  // en móvil, un handler de scroll en una galería de miles de fotos es la
  // forma más rápida de que la página vaya a tirones.
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadPage(false)
      },
      // Precarga antes de llegar al final: con RTT de ~80 ms desde Lima,
      // esperar a tocar el borde se nota como un tirón.
      { rootMargin: '600px' },
    )

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [loadPage])

  const toggleSelect = async (asset: SharedAsset) => {
    const next = !asset.selected

    /*
      Una foto ya descargada no se desmarca, y se dice en vez de fallar.

      Se para aquí y no en el servidor —que también lo impide— porque el
      optimismo de abajo apagaría la estrella en pantalla mientras la fila sigue
      contando, y el cliente vería el cupo gastado sin nada marcado.
    */
    if (!next && asset.delivered) {
      setError('Esta foto ya la has descargado, así que sigue contando para tu selección.')

      return
    }

    // Optimista: marcar un favorito debe sentirse instantáneo aunque el
    // servidor esté a 80 ms.
    setAssets((previous) =>
      previous.map((a) => (a.id === asset.id ? { ...a, selected: next } : a)),
    )

    try {
      /*
        El cupo lo dice el servidor, no se calcula aquí.

        Es de la ENTREGA: marcar algo que la otra persona ya marcó no lo sube, y
        soltar algo ya descargado no lo baja. Sumar uno a ojo —que con un cupo
        por visitante era exacto— ahora daría números que no cuadran con el
        siguiente refresco.
      */
      setQuota(next
        ? await selectAsset(session.token, asset.id)
        : await deselectAsset(session.token, asset.id))
      setError(null)
    } catch (caught) {
      setAssets((previous) =>
        previous.map((a) => (a.id === asset.id ? { ...a, selected: !next } : a)),
      )

      // El límite de selección NO es un fallo técnico: es el cupo que contrató
      // el cliente. El mensaje tiene que decirlo con esas palabras.
      setError(
        caught instanceof ApiException && caught.error.code === 'share_selection_limit'
          ? caught.error.message
          : 'No se pudo guardar tu selección.',
      )
    }
  }

  const download = async (ids: string[]) => {
    if (ids.length === 0) return

    setDownloading(true)
    setError(null)

    try {
      // Una sola foto: descarga directa, sin esperar a un worker.
      if (ids.length === 1) {
        const [entry] = await requestDownloads(session.token, ids)
        if (entry) triggerDownload(entry.url, entry.filename)

        return
      }

      // Varias: ZIP. Descargar 40 archivos uno a uno es intolerable en móvil,
      // porque los navegadores bloquean descargas simultáneas y el cliente se
      // queda con la mitad sin enterarse.
      setDownloadNote('Preparando tu descarga…')
      const bundle = await requestBundle(session.token, ids)

      /*
       | Sondeo con espera creciente: un ZIP de 1 GB tarda, y machacar el
       | servidor cada segundo desde un móvil sólo gasta batería y datos.
       |
       | La PRIMERA espera es corta a propósito. La original era de un segundo,
       | y medido contra el worker un ZIP de tres fotos está listo en 47 ms: el
       | cliente se quedaba mirando «Preparando tu descarga…» casi un segundo
       | entero por nada. No es un caso raro — en un proofing con cupo de cinco
       | fotos es el caso NORMAL. La progresión sigue creciendo igual de rápido
       | a partir de ahí, así que un ZIP grande no sondea ni una vez más.
       */
      for (let attempt = 0; attempt < 60; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, Math.min(250 + attempt * 750, 5000)))

        const status = await fetchBundleStatus(session.token, bundle.bundle_id)

        if (status.status === 'ready' && status.url !== null) {
          triggerDownload(status.url, 'fotos.zip')
          setDownloadNote(null)

          return
        }

        if (status.status === 'failed') {
          setError(status.error ?? 'No se pudo preparar el archivo.')
          setDownloadNote(null)

          return
        }
      }

      setDownloadNote(null)
      setError('La descarga está tardando más de lo normal. Inténtalo en unos minutos.')
    } catch (caught) {
      setDownloadNote(null)
      setError(
        caught instanceof ApiException ? caught.error.message : 'No se pudo preparar la descarga.',
      )
    } finally {
      setDownloading(false)
    }
  }

  const triggerDownload = (url: string, filename: string): void => {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    // target=_blank además del atributo download: `download` se IGNORA en URLs
    // de otro origen, y el almacenamiento lo es. La descarga funciona igual
    // porque la URL firmada lleva Content-Disposition: attachment, pero si
    // algún navegador decidiera navegar, lo haría en otra pestaña en vez de
    // sacar al cliente de la galería.
    anchor.target = '_blank'
    anchor.rel = 'noopener'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  }

  if (project === null) {
    // Sin proyecto no se sabe ni el tema ni el nombre del estudio, así que el
    // esqueleto es todo lo que se puede prometer con honestidad. Aun así llega
    // con la retícula ya dibujada: cuando entren las fotos, nada se moverá.
    return error !== null ? (
      <ErrorState
        icon={ImageOff}
        title="No se pudo abrir la galería"
        description={error}
        /*
         | Sin «Reintentar» cuando no hay nada que reintentar: una entrega
         | caducada no se arregla recargando, y ofrecerlo manda al cliente a
         | pulsar un botón que nunca va a funcionar.
         */
        action={
          error === RETRYABLE ? (
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Reintentar
            </Button>
          ) : undefined
        }
        className="min-h-dvh justify-center"
      />
    ) : (
      <div className="min-h-dvh pt-safe-4">
        <div className="flex flex-col items-center gap-2 px-4 py-4">
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-7 w-56" />
        </div>
        <GallerySkeleton />
      </div>
    )
  }

  /*
   | El saludo del enlace SUSTITUYE a la descripción del proyecto, no se suma.
   |
   | Ocupan el mismo sitio —la línea bajo el título— y son la misma clase de
   | cosa dicha a distinta escala: la descripción vale para toda la sesión, el
   | saludo es para este cliente. Cuando el estudio se ha molestado en escribir
   | uno, apilarle encima el texto genérico del proyecto lo desluce.
   */
  const intro = project.welcome_message ?? project.description

  const studioMark = project.studio.branding.logo_url ? (
    <img
      src={project.studio.branding.logo_url}
      alt={project.studio.name}
      className="mx-auto h-10 object-contain"
    />
  ) : (
    <p className="text-label font-medium uppercase tracking-wide text-ink-subtle">
      {project.studio.name}
    </p>
  )

  /*
   | Enlace de recepción: no hay galería, hay un sitio donde soltar archivos.
   |
   | Se sale por aquí antes de la galería en vez de esconder trozos con
   | condicionales: lo que ve este cliente no es una entrega con la retícula
   | apagada, es otra pantalla. Comparte la marca del estudio y el saludo,
   | que es lo que hace que reconozca a quién le está mandando sus cosas.
   |
   | Sin portada: es una foto, y el servidor ya no la firma sin `view`.
   */
  if (!canView) {
    return (
      <div className="min-h-dvh pb-safe-action">
        <header className="px-4 py-5 pt-safe-4 text-center sm:px-6">
          {studioMark}
          <h1 className="mt-4 text-title font-semibold text-ink">{project.name}</h1>
          {intro !== null && intro !== '' && (
            <p className="mx-auto mt-2 max-w-prose text-body text-ink-muted">{intro}</p>
          )}
        </header>

        <main className="mx-auto max-w-prose px-4 pb-10 sm:px-6">
          {canUpload ? (
            <SendFilesPanel sessionToken={session.token} projectId={project.id} />
          ) : (
            /*
             | Ni ver ni enviar: el enlace no autoriza nada.
             |
             | No debería poder crearse desde el panel, pero la API acepta
             | cualquier combinación de permisos y esta pantalla no puede
             | quedarse en blanco por ello.
             */
            <ErrorState
              icon={ImageOff}
              title="Este enlace no tiene nada que enseñarte"
              description="Pídele a quien te lo mandó que te envíe uno nuevo."
              className="justify-center"
            />
          )}
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-dvh pb-safe-action">
      <header>
        {/*
          El logo va SIEMPRE sobre la superficie, nunca encima de la portada.
          Es la única pieza de esta pantalla cuyo color no controlamos: un logo
          negro sobre una foto oscurecida desaparece, y con marca blanca eso le
          pasaría justo al estudio que más cuida su marca. Nuestro texto sí
          puede ir encima, porque lo pintamos nosotros en blanco.
        */}
        <div className="px-4 py-5 pt-safe-4 text-center sm:px-6">{studioMark}</div>

        {project.cover_url !== null ? (
          // El fondo hundido va DEBAJO de la foto a propósito: mientras la
          // portada viaja —y en una conexión peruana viaja despacio— el hueco
          // se ve del mismo gris que el resto de marcadores de la galería, en
          // vez de un bloque blanco bajo el velo oscuro.
          <div className="relative bg-surface-sunken">
            <img
              src={project.cover_url}
              // Decorativa: la misma foto está abajo en la retícula con su
              // nombre, y quien no ve la imagen ya tiene el título al lado.
              alt=""
              /*
               | Sin `lazy` y con prioridad alta: es lo primero que se ve y la
               | imagen que marca la LCP de la página. Es la excepción a la
               | regla del resto de la galería, donde `lazy` es lo que impide
               | reventar los datos móviles del cliente.
               */
              fetchPriority="high"
              decoding="async"
              className="h-[46svh] max-h-[520px] min-h-56 w-full object-cover sm:h-[54svh]"
            />

            {/*
              Velo de negro, no un tema: encima de una foto el tema no vale
              porque el fondo es la imagen, y una imagen puede ser de cualquier
              color. Con degradado, para que el texto de abajo se lea sobre un
              cielo blanco sin apagar la foto entera.
            */}
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent"
            />

            <div className="absolute inset-x-0 bottom-0 px-5 pb-6 text-center sm:px-8 sm:pb-8">
              {/* `title` y no `text-xl`: es el nombre de la boda de alguien y
                  el elemento con más carga emocional de todo el producto. */}
              <h1 className="text-title font-semibold text-white">{project.name}</h1>

              {intro !== null && (
                <p className="mx-auto mt-2 max-w-prose text-body text-white/85">{intro}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="px-4 pb-1 text-center sm:px-6">
            <h1 className="text-title font-semibold text-ink">{project.name}</h1>

            {intro !== null && (
              <p className="mx-auto mt-2 max-w-prose text-body text-ink-muted">{intro}</p>
            )}
          </div>
        )}

        <div className="px-4 py-4 text-center sm:px-6">
          <p className="text-caption text-ink-subtle" data-numeric>
            {project.asset_count} archivos
          </p>

          {canSelect && quota.max !== null && (
            /*
              El cliente contrató N fotos editadas: tiene que ver cuántas le
              quedan mientras elige, no descubrirlo al chocar con el límite.

              «Elegidas» y no «has elegido»: el cupo es de la ENTREGA, así que
              en una boda que abren los dos el número sube también cuando marca
              el otro. En segunda persona parecería que la cuenta está mal.
            */
            <p
              className="mt-3 inline-block rounded-full bg-accent-subtle px-3 py-1 text-label font-medium text-accent"
              data-numeric
            >
              Elegidas {quota.used} de {quota.max}
            </p>
          )}
        </div>
      </header>

      {/*
        Justo debajo de la cabecera y encima de la galería, no al final.
        En un enlace de recepción enviar ES la tarea; dejarlo tras cuatrocientas
        miniaturas lo esconde detrás de lo que el cliente no venía a hacer.
      */}
      {canUpload && <SendFilesPanel sessionToken={session.token} projectId={project.id} />}

      {canSelect && (
        // Segmentado, no dos botones sueltos: así se lee como un interruptor de
        // dos posiciones y queda claro que son excluyentes.
        <div className="flex justify-center px-4 pb-4">
          <div className="inline-flex gap-1 rounded-full bg-surface-sunken p-1">
            <button
              onClick={() => changeFilter(false)}
              aria-pressed={!onlySelected}
              className={cx(FILTER_TAB, !onlySelected ? FILTER_TAB_ON : FILTER_TAB_OFF)}
            >
              Todas
            </button>
            <button
              onClick={() => changeFilter(true)}
              aria-pressed={onlySelected}
              className={cx(FILTER_TAB, onlySelected ? FILTER_TAB_ON : FILTER_TAB_OFF)}
            >
              {/* «Mis», porque esta lista SÍ es personal: la de arriba es la
                  cuenta de la entrega y ésta es la de quien mira. */}
              Mis favoritas{selectedCount > 0 && ` (${selectedCount})`}
            </button>
          </div>
        </div>
      )}

      {error !== null && (
        <p role="alert" className="px-4 pb-3 text-center text-body text-danger">
          {error}
        </p>
      )}

      {readsAsList ? (
        <DocumentList
          assets={assets}
          onOpen={(asset) => setViewerIndex(assets.findIndex((x) => x.id === asset.id))}
        />
      ) : (
        /* 2 columnas en móvil: con 3 las fotos quedan demasiado pequeñas para
           decidir si una sale bien, que es exactamente lo que viene a hacer. */
        <ul className="grid grid-cols-2 gap-1.5 px-1.5 xs:gap-2 xs:px-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {assets.map((asset) => (
            <AssetTile
              key={asset.id}
              asset={asset}
              canSelect={canSelect}
              quotaReached={quota.max !== null && quota.used >= quota.max}
              onToggleSelect={(a) => void toggleSelect(a)}
              onOpen={() => setViewerIndex(assets.findIndex((x) => x.id === asset.id))}
            />
          ))}
        </ul>
      )}

      <div ref={sentinelRef} className="h-10" />

      {/* Al paginar el esqueleto va DEBAJO de lo ya cargado, con la forma de la
          siguiente fila: se ve que viene más en vez de una frase gris al pie. */}
      {loading && assets.length > 0 && <GallerySkeleton count={5} />}

      {assets.length === 0 &&
        !loading &&
        (onlySelected ? (
          <EmptyState
            icon={Star}
            title="Todavía no has elegido ninguna"
            description="Toca una foto para verla en grande y marca las que quieras con la estrella."
            action={
              <Button variant="secondary" onClick={() => changeFilter(false)}>
                Ver todas las fotos
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={ImageOff}
            title="Esta galería está vacía"
            description={`${project.studio.name} todavía no ha subido nada aquí. Escríbeles si esperabas encontrar fotos.`}
          />
        ))}

      {viewerIndex !== null && (
        <Lightbox
          assets={assets}
          index={viewerIndex}
          canSelect={canSelect}
          canDownload={canDownload}
          quotaReached={quota.max !== null && quota.used >= quota.max}
          onIndexChange={setViewerIndex}
          onToggleSelect={(a) => void toggleSelect(a)}
          onDownload={(a) => void download([a.id])}
          onClose={() => setViewerIndex(null)}
          onNeedMore={() => void loadPage(false)}
        />
      )}

      {/* Barra de acción fija: en móvil tiene que estar donde llega el pulgar,
          no al final de una galería de 400 fotos. */}
      {canDownload && selectedCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 border-t border-line bg-surface/95 px-4 py-3 pb-safe-3 backdrop-blur">
          <Button
            size="lg"
            block
            busy={downloading && (downloadNote ?? 'Preparando…')}
            onClick={() => void download(assets.filter((a) => a.selected).map((a) => a.id))}
          >
            <Download size={18} strokeWidth={ICON_STROKE} aria-hidden />
            Descargar {selectedCount} favorita{selectedCount === 1 ? '' : 's'}
          </Button>
        </div>
      )}

      {/*
        El Libro de Reclamaciones DEL ESTUDIO (§1).

        Aquí el proveedor es él —vendió la sesión, cobró y responde—, así que un
        enlace a NUESTRO libro mandaría a este cliente a reclamar contra quien no
        le vendió nada. Y el obligado a tenerlo es él: el reglamento pide que en
        un comercio virtual esté accesible, y esta galería es el único sitio
        donde su cliente se encuentra con él.

        Sólo aparece si el estudio lo tiene dado de alta. Un enlace roto bajo el
        rótulo «Libro de Reclamaciones» no es una carencia, es afirmar que ese
        proveedor no lo tiene.

        Al pie y discreto a propósito: quien viene a ver sus fotos no tiene que
        tropezarse con esto, y quien lo busca sabe que se busca abajo.
      */}
      {project.studio.complaints_book_url !== null && (
        <footer className="mt-12 border-t border-line px-4 py-6 text-center sm:px-6">
          <a
            href={project.studio.complaints_book_url}
            target="_blank"
            rel="noopener noreferrer"
            className="action inline-flex items-center gap-2 text-label text-ink-subtle underline-offset-4 hover:underline"
          >
            <BookText size={16} strokeWidth={ICON_STROKE} aria-hidden />
            Libro de Reclamaciones de {project.studio.name}
          </a>
        </footer>
      )}
    </div>
  )
}
